<template>
  <div class="insights-trends">
    <h2 class="insights-trends__title">Proposal volume over time</h2>
    <p class="insights-trends__intro">
      Number of proposals (invoice lines) produced each day in the selected period. Useful
      for spotting unusual spikes or drops in activity.
    </p>

    <q-skeleton v-if="loading" animation="blink" height="320px" />
    <div v-else-if="!hasData" class="insights-trends__empty">
      <q-icon name="show_chart" size="32px" color="blue-grey-2" />
      <span>No proposals in this period.</span>
    </div>
    <apexchart
      v-else
      type="line"
      height="320"
      :options="chartOptions"
      :series="series"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { InsightsTrendsResult } from '../types';

const props = defineProps<{
  data: InsightsTrendsResult | null;
  loading: boolean;
}>();

const hasData = computed(() =>
  (props.data?.dailyTrends?.length ?? 0) > 0
);

const series = computed(() => [
  {
    name: 'Proposals',
    data: props.data?.dailyTrends.map(d => d.total) ?? []
  }
]);

const chartOptions = computed(() => ({
  chart: {
    type: 'line',
    toolbar: { show: false },
    zoom: { enabled: false }
  },
  colors: ['#1aacff'],
  stroke: { curve: 'smooth', width: 2 },
  xaxis: {
    type: 'datetime',
    categories: props.data?.dailyTrends.map(d => d.date) ?? []
  },
  dataLabels: { enabled: false },
  tooltip: { x: { format: 'yyyy-MM-dd' } }
}));
</script>

<style scoped lang="scss">
.insights-trends {
  &__title {
    margin: 0 0 4px;
    font-size: 18px;
    font-weight: 600;
    color: #2c2c2c;
  }

  &__intro {
    margin: 0 0 12px;
    font-size: 13px;
    color: #4c4c4c;
  }

  &__empty {
    height: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #8b8b8b;
    font-size: 13px;
  }
}
</style>
